"use client";

import { motion } from "framer-motion";
import { GraduationCap, BookOpen, Wrench, Users, Award, Briefcase, Sprout, FlaskConical } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const programs = [
  {
    icon: Sprout,
    title: "Agronomie des Racines",
    duration: "6 mois",
    level: "Initiation",
    desc: "Cycle complet de culture : choix des semences, préparation des sols, irrigation, lutte biologique, récolte optimale. Spécialisation patate douce, manioc, igname.",
    modules: ["Botanique appliquée", "Sol & fertilisation", "Calendrier cultural", "Lutte biologique"],
  },
  {
    icon: FlaskConical,
    title: "Transformation Agroalimentaire",
    duration: "9 mois",
    level: "Professionnel",
    desc: "Maîtrise des procédés : râpage, fermentation, séchage, cuisson, conditionnement. Normes HACCP, hygiène, gestion qualité. Stages en usine.",
    modules: ["Procédés de transformation", "Hygiène & HACCP", "Contrôle qualité", "Maintenance équipements"],
  },
  {
    icon: Briefcase,
    title: "Entrepreneuriat Agricole",
    duration: "4 mois",
    level: "Avancé",
    desc: "Créer et gérer une exploitation ou une unité de transformation. Business plan, financement, comptabilité, marketing, commercialisation.",
    modules: ["Business plan", "Financement & microcrédit", "Comptabilité", "Marketing & vente"],
  },
];

const stats = [
  { icon: Users, value: "320+", label: "Apprentis formés" },
  { icon: Award, value: "85%", label: "Taux d'insertion" },
  { icon: BookOpen, value: "18", label: "Modules de cours" },
  { icon: Wrench, value: "04", label: "Ateliers pratiques" },
];

export function Ecole() {
  return (
    <section id="ecole" className="relative py-24 lg:py-32 bg-gradient-to-b from-secondary/40 to-background">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left intro */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="divider-gold max-w-xs mb-5"
            >
              <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
                École de Formation
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
            >
              Transmettre le <span className="text-gradient-gold">savoir</span> de la
              terre
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base sm:text-lg text-foreground/75 leading-relaxed"
            >
              La maison PATATE OR forme la prochaine génération d&apos;agronomes,
              transformateurs et entrepreneurs agricoles. Notre école, ancrée dans le
              terrain, combine savoirs traditionnels et technologies modernes pour bâtir
              une souveraineté alimentaire durable.
            </motion.p>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 relative rounded-3xl overflow-hidden shadow-gold-lg ring-1 ring-gold/30"
            >
              <img
                src="/images/formation-pratique.png"
                alt="Formation pratique en atelier"
                className="w-full h-72 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <Badge className="bg-gradient-gold text-white border-0 shadow-gold">
                  <GraduationCap className="w-3 h-3 mr-1" />
                  Formation pratique
                </Badge>
                <p className="mt-2 text-cream text-sm font-medium italic">
                  « Le savoir se transmet par la main, pas seulement par le livre. »
                </p>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
            >
              {stats.map((s, i) => (
                <div key={i} className="p-4 rounded-xl bg-card border border-border text-center hover:border-gold transition-colors">
                  <s.icon className="w-5 h-5 text-gold mx-auto" />
                  <div className="mt-2 font-display text-xl font-extrabold text-gradient-gold">
                    {s.value}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right programs */}
          <div className="lg:col-span-7 space-y-6">
            {programs.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="group relative p-6 sm:p-8 rounded-3xl bg-card border border-border hover:border-gold hover:shadow-gold transition-all duration-500 overflow-hidden"
              >
                <div className="absolute top-0 right-0 text-9xl font-display font-black text-gold/5 group-hover:text-gold/10 transition-colors">
                  {String(i + 1).padStart(2, "0")}
                </div>

                <div className="relative">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="shrink-0 grid place-items-center w-14 h-14 rounded-2xl bg-gradient-gold shadow-gold group-hover:scale-110 transition-transform">
                      <p.icon className="w-7 h-7 text-white" strokeWidth={1.8} />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-display text-2xl font-bold text-foreground">
                          {p.title}
                        </h3>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary text-[10px] font-semibold uppercase tracking-wider text-gold-deep">
                          {p.duration}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary text-[10px] font-semibold uppercase tracking-wider text-gold-deep">
                          {p.level}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {p.desc}
                  </p>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {p.modules.map((m, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs text-foreground/80">
                        <BookOpen className="w-3.5 h-3.5 text-gold-deep shrink-0" />
                        <span>{m}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="p-6 rounded-3xl bg-gradient-gold text-white text-center shadow-gold"
            >
              <GraduationCap className="w-10 h-10 mx-auto mb-3" />
              <h3 className="font-display text-xl font-bold">
                Candidatures ouvertes toute l&apos;année
              </h3>
              <p className="mt-2 text-white/90 text-sm">
                Bourses disponibles pour les méritants. Stages garantis dans nos
                installations.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
